import React, { useEffect, useMemo, useState } from 'react';
import { XIcon, CheckIcon, ChevronDownIcon, Minus, Plus } from 'lucide-react';
import { MenuItem, MenuItemOption, CartItemOption } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import { useMenuItemOptions } from '../../lib/hooks';
const EXTRA_SALSA_PRICE = 350;
function stripEmbeddedPrice(name: string): string {
  return name.replace(/\s*[—–-]\s*₡[\d.,\s]+$/, '').trim();
}
function safeMaxSelect(val: number | null | undefined): number {
  return val && val > 0 ? val : 1;
}
type BucketId = 'rolls' | 'onigiri' | 'esfera' | 'mochis' | 'proteinas' | 'vegetales' | 'opciones' | 'salsas' | 'other';
function classifyGroup(name: string): BucketId {
  if (/salsa/i.test(name)) return 'salsas';
  if (/proteína|proteinas|proteínas|protein/i.test(name)) return 'proteinas';
  if (/vegetal|veget/i.test(name)) return 'vegetales';
  if (/topping|opciones\s+sushi\s+cake|opciones/i.test(name)) return 'opciones';
  // sushi-style item families collapse into shared merged buckets so
  // duplicate per-slot groups in the DB ("Mochis 1", "Mochi 2"…) merge.
  if (/onigiri/i.test(name)) return 'onigiri';
  if (/esfera/i.test(name)) return 'esfera';
  if (/mochi/i.test(name)) return 'mochis';
  if (/(^|\s)roll(s)?\b|sushi(?!\s+cake)/i.test(name)) return 'rolls';
  return 'other';
}
/**
 * Normalize a DB option_group name so that singular/plural variants and
 * "Combo X" / "sushi cake" suffixes collapse to the same logical slot.
 *
 *   "Mochis 1 Combo 5" ─┐
 *   "Mochi 1 Combo 5"  ─┴─► "mochi 1"
 *   "Proteína 2 sushi cake" ─► "proteína 2"
 *   "Salsa 1 Combo 3" ──────► "salsa 1"
 *   "Elige tus salsas" ─────► "elige tu salsa"
 */
function normalizeGroupName(name: string): string {
  let s = name.toLowerCase().replace(/\s*combo\s*\d+\s*$/i, '').replace(/\s+sushi\s+cake\s*$/i, '').trim();
  const m = s.match(/^(.+?)s?\s+(\d+)\s*$/i);
  if (m) {
    const base = m[1].replace(/s$/, '').trim();
    return `${base} ${m[2]}`;
  }
  return s.replace(/s\s*$/i, '').trim();
}
interface MergedBucket {
  id: BucketId;
  label: string;
  options: MenuItemOption[];
  /** Free / included count */
  includedCount: number;
  /** Allow picking beyond includedCount? */
  allowExtras: boolean;
  /** Per-unit price for extras beyond includedCount (only used if allowExtras) */
  extraPrice: number;
  /** Original group names this bucket merges — used for cart payload */
  groupNames: string[];
}
interface SingleSlot {
  key: string;
  groupName: string;
  label: string;
  options: MenuItemOption[];
  selectionMode?: string;
}
interface KyomuComboModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  onAdd: (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => void;
}
/**
 * Kyomu combo selection modal.
 *
 * Two layout primitives:
 *   1) Single-pick dropdown slots (Cabaña-style) for "other" groups
 *      that don't fit a known category — e.g. rolls, onigiri, esfera,
 *      mochis. Each group with max_select=1 becomes one dropdown; groups
 *      with max_select>1 expand into N dropdowns.
 *   2) Merged multi-pick buckets for groups that share a category:
 *      Proteínas, Vegetales, Opciones, Salsas. All groups in the same
 *      category are de-duplicated by option name and rendered as ONE
 *      section with a [-] qty [+] picker. Included count is computed
 *      robustly from the underlying group(s).
 *
 * Salsas is the only bucket that allows extras (+₡350 per salsa above
 * the included count). Proteínas/Vegetales/Opciones are hard-capped.
 */
export function KyomuComboModal({
  isOpen,
  onClose,
  item,
  onAdd
}: KyomuComboModalProps) {
  const [notes, setNotes] = useState('');
  const [openSlotKey, setOpenSlotKey] = useState<string | null>(null);
  // slotKey -> selected MenuItemOption for the non-merged slots
  const [picks, setPicks] = useState<Record<string, MenuItemOption | null>>({});
  // bucketId -> (optionId -> quantity)
  const [bucketPicks, setBucketPicks] = useState<Record<BucketId, Record<string, number>>>({
    rolls: {},
    onigiri: {},
    esfera: {},
    mochis: {},
    proteinas: {},
    vegetales: {},
    opciones: {},
    salsas: {},
    other: {}
  });
  const {
    options,
    loading
  } = useMenuItemOptions(item?.id);
  useEffect(() => {
    if (isOpen && item) {
      setNotes('');
      setOpenSlotKey(null);
      setPicks({});
      setBucketPicks({
        rolls: {},
        onigiri: {},
        esfera: {},
        mochis: {},
        proteinas: {},
        vegetales: {},
        opciones: {},
        salsas: {},
        other: {}
      });
    }
  }, [isOpen, item?.id]);
  // Group options by option_group
  const groupedOptions = useMemo(() => {
    return options.reduce((acc, option) => {
      const group = option.option_group;
      if (!acc[group]) acc[group] = [];
      acc[group].push(option);
      return acc;
    }, {} as Record<string, MenuItemOption[]>);
  }, [options]);
  // Filter: drop extras / adicionales (those move to checkout upsell)
  const comboGroups = useMemo(() => {
    return Object.entries(groupedOptions).filter(([groupName]) => {
      if (/(extra|adicional|adicion)/i.test(groupName)) return false;
      return true;
    });
  }, [groupedOptions]);
  // Bucketize by category
  const groupsByBucket = useMemo(() => {
    const map: Record<BucketId, [string, MenuItemOption[]][]> = {
      rolls: [],
      onigiri: [],
      esfera: [],
      mochis: [],
      proteinas: [],
      vegetales: [],
      opciones: [],
      salsas: [],
      other: []
    };
    comboGroups.forEach(entry => {
      const id = classifyGroup(entry[0]);
      map[id].push(entry);
    });
    return map;
  }, [comboGroups]);
  /**
   * Compute the included count for a merged bucket.
   *
   * Two-tier rule to avoid double-counting when the DB has duplicate or
   * overlapping groups describing the same logical slots:
   *
   *   1) If any group has max_select > 1, take the LARGEST such
   *      max_select as authoritative. Rationale: a multi-pick group
   *      like "Elige tus salsas" (max=3) supersedes the per-slot
   *      decomposition that lives alongside it ("Salsa 1", "Salsa 2",
   *      "Salsa 3" each max=1).
   *
   *   2) Otherwise, every group has max_select=1. Count the number of
   *      LOGICAL groups (normalized: "Mochis 1 Combo 5" and
   *      "Mochi 1 Combo 5" collapse to one logical "mochi 1"). This way
   *      duplicate singular/plural variants merge instead of summing.
   */
  const includedForGroups = (entries: [string, MenuItemOption[]][]) => {
    const maxMulti = entries.filter(([, opts]) => safeMaxSelect(opts[0]?.max_select) > 1).reduce((mx, [, opts]) => Math.max(mx, safeMaxSelect(opts[0]?.max_select)), 0);
    if (maxMulti > 0) return maxMulti;
    // All single-pick: dedupe by normalized name and count logical groups
    const logical = new Set<string>();
    entries.forEach(([name]) => {
      logical.add(normalizeGroupName(name));
    });
    return logical.size;
  };
  const dedupeOptions = (entries: [string, MenuItemOption[]][]) => {
    const seen = new Map<string, MenuItemOption>();
    entries.forEach(([, opts]) => {
      opts.forEach(opt => {
        const key = stripEmbeddedPrice(opt.name).toLowerCase().trim();
        if (!seen.has(key)) seen.set(key, opt);
      });
    });
    return Array.from(seen.values());
  };
  // Build merged buckets (only for categories that have groups)
  const mergedBuckets = useMemo<MergedBucket[]>(() => {
    const out: MergedBucket[] = [];
    const buildBucket = (id: Exclude<BucketId, 'other'>, label: string, allowExtras: boolean, extraPrice: number, forcedCount?: number): MergedBucket | null => {
      const entries = groupsByBucket[id];
      if (entries.length === 0) return null;
      return {
        id,
        label,
        options: dedupeOptions(entries),
        includedCount: forcedCount ?? includedForGroups(entries),
        allowExtras,
        extraPrice,
        groupNames: entries.map(([name]) => name)
      };
    };
    const order: {
      id: Exclude<BucketId, 'other'>;
      label: string;
      allowExtras: boolean;
      extraPrice: number;
      forcedCount?: number;
    }[] = [{
      id: 'rolls',
      label: 'Rolls',
      allowExtras: false,
      extraPrice: 0
    }, {
      id: 'onigiri',
      label: 'Onigiri',
      allowExtras: false,
      extraPrice: 0
    }, {
      id: 'esfera',
      label: 'Esfera',
      allowExtras: false,
      extraPrice: 0
    }, {
      id: 'mochis',
      label: 'Mochis',
      allowExtras: false,
      extraPrice: 0
    }, {
      id: 'proteinas',
      label: 'Proteínas',
      allowExtras: false,
      extraPrice: 0
    }, {
      id: 'vegetales',
      label: 'Vegetales',
      allowExtras: false,
      extraPrice: 0
    }, {
      // "Opciones sushi cake" is hard-capped at 1 by product rule,
      // regardless of what max_select says in the DB.
      id: 'opciones',
      label: 'Opciones sushi cake',
      allowExtras: false,
      extraPrice: 0,
      forcedCount: 1
    }, {
      id: 'salsas',
      label: 'Salsas',
      allowExtras: true,
      extraPrice: EXTRA_SALSA_PRICE
    }];
    order.forEach(cfg => {
      const b = buildBucket(cfg.id, cfg.label, cfg.allowExtras, cfg.extraPrice, cfg.forcedCount);
      if (b) out.push(b);
    });
    return out;
  }, [groupsByBucket]);
  // Build single-pick slots for "other" groups (rolls, onigiri, esfera, mochis…)
  const otherSlots = useMemo<SingleSlot[]>(() => {
    const sorted = [...groupsByBucket.other];
    sorted.sort((a, b) => {
      const orderA = a[1][0]?.sort_order ?? 0;
      const orderB = b[1][0]?.sort_order ?? 0;
      return orderA - orderB;
    });
    const out: SingleSlot[] = [];
    sorted.forEach(([groupName, groupOpts]) => {
      const firstOpt = groupOpts[0];
      const maxSel = safeMaxSelect(firstOpt.max_select);
      const selectionMode = firstOpt.selection_mode || 'standard';
      if (maxSel === 1) {
        const cleanLabel = groupName.replace(/\s*combo\s*\d+\s*$/i, '').trim();
        out.push({
          key: `${groupName}__0`,
          groupName,
          label: (cleanLabel || groupName).toUpperCase(),
          options: groupOpts,
          selectionMode
        });
      } else {
        const base = groupName.replace(/\s*combo\s*\d+\s*$/i, '').replace(/^elige\s+\d*\s*(tus|tu|las|los)?\s*/i, '').trim();
        const cap = base.charAt(0).toUpperCase() + base.slice(1);
        for (let i = 0; i < maxSel; i++) {
          out.push({
            key: `${groupName}__${i}`,
            groupName,
            label: `${cap} ${i + 1}`.toUpperCase(),
            options: groupOpts,
            selectionMode
          });
        }
      }
    });
    return out;
  }, [groupsByBucket]);
  // Totals per bucket
  const bucketTotals = useMemo(() => {
    const map: Record<BucketId, number> = {
      rolls: 0,
      onigiri: 0,
      esfera: 0,
      mochis: 0,
      proteinas: 0,
      vegetales: 0,
      opciones: 0,
      salsas: 0,
      other: 0
    };
    (Object.keys(bucketPicks) as BucketId[]).forEach(id => {
      map[id] = Object.values(bucketPicks[id] || {}).reduce((a, b) => a + b, 0);
    });
    return map;
  }, [bucketPicks]);
  // Salsa extras pricing
  const salsasBucket = mergedBuckets.find(b => b.id === 'salsas');
  const salsasIncluded = salsasBucket?.includedCount || 0;
  const totalSalsas = bucketTotals.salsas;
  const extraSalsas = salsasBucket?.allowExtras ? Math.max(0, totalSalsas - salsasIncluded) : 0;
  const extraSalsasTotal = extraSalsas * EXTRA_SALSA_PRICE;
  // Validity
  const allSlotsPicked = otherSlots.every(s => !!picks[s.key]);
  const allBucketsSatisfied = mergedBuckets.every(b => bucketTotals[b.id] >= b.includedCount);
  const allPicked = allSlotsPicked && allBucketsSatisfied;
  // Pricing for non-bucket option deltas (rolls upgrades etc.)
  const slotOptionsTotal = otherSlots.reduce((sum, s) => {
    const opt = picks[s.key];
    return sum + (opt ? Number(opt.price_delta) || 0 : 0);
  }, 0);
  const totalPrice = (Number(item?.price) || 0) + slotOptionsTotal + extraSalsasTotal;
  if (!isOpen || !item) return null;
  const setSlotPick = (slotKey: string, option: MenuItemOption) => {
    setPicks(prev => ({
      ...prev,
      [slotKey]: option
    }));
    setOpenSlotKey(null);
  };
  const incrementBucket = (bucket: MergedBucket, opt: MenuItemOption) => {
    setBucketPicks(prev => {
      const current = prev[bucket.id][opt.id] || 0;
      const total = Object.values(prev[bucket.id]).reduce((a, b) => a + b, 0);
      // Hard cap when extras aren't allowed
      if (!bucket.allowExtras && total >= bucket.includedCount) return prev;
      return {
        ...prev,
        [bucket.id]: {
          ...prev[bucket.id],
          [opt.id]: current + 1
        }
      };
    });
  };
  const decrementBucket = (bucket: MergedBucket, opt: MenuItemOption) => {
    setBucketPicks(prev => {
      const current = prev[bucket.id][opt.id] || 0;
      if (current <= 0) return prev;
      const next = current - 1;
      const newBucket = {
        ...prev[bucket.id]
      };
      if (next === 0) delete newBucket[opt.id];else newBucket[opt.id] = next;
      return {
        ...prev,
        [bucket.id]: newBucket
      };
    });
  };
  const handleAdd = () => {
    if (!allPicked) return;
    const cartOptions: CartItemOption[] = [];
    // Single-pick slots
    otherSlots.forEach(s => {
      const opt = picks[s.key];
      if (!opt) return;
      cartOptions.push({
        option_name: opt.name,
        option_group: s.groupName,
        selection_mode: s.selectionMode,
        price_delta: Number(opt.price_delta) || 0
      });
    });
    // Merged buckets — flatten by quantity. For salsas, first N go free,
    // rest get +EXTRA_SALSA_PRICE. For other buckets, all are free.
    mergedBuckets.forEach(bucket => {
      const picksMap = bucketPicks[bucket.id];
      const flat: MenuItemOption[] = [];
      Object.entries(picksMap).forEach(([optId, qty]) => {
        const opt = bucket.options.find(o => o.id === optId);
        if (!opt) return;
        for (let i = 0; i < qty; i++) flat.push(opt);
      });
      flat.forEach((opt, idx) => {
        const isExtra = bucket.allowExtras && idx >= bucket.includedCount;
        cartOptions.push({
          option_name: opt.name,
          option_group: isExtra ? `${bucket.label} extra` : bucket.label,
          selection_mode: 'quantity',
          price_delta: isExtra ? bucket.extraPrice : 0
        });
      });
    });
    onAdd(item, 1, cartOptions, notes.trim());
    onClose();
  };
  const renderBucket = (bucket: MergedBucket) => {
    const total = bucketTotals[bucket.id];
    const remaining = Math.max(0, bucket.includedCount - total);
    const extras = bucket.allowExtras ? Math.max(0, total - bucket.includedCount) : 0;
    const extrasTotal = extras * bucket.extraPrice;
    const satisfied = total >= bucket.includedCount;
    return <div key={bucket.id} className="bg-[#141414] border border-white/10 rounded-2xl p-4 space-y-3" data-id="element-2834" data-mp="yz0r0lwuyers">
        <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-2835" data-mp="1cy0ehq8mwnmd">
          <div className="min-w-0" data-id="element-2836" data-mp="18106h9s85asm">
            <p className="text-xs font-bold uppercase tracking-wider text-[#C9A34E]" data-id="element-2837" data-mp="1mpwo9yk3226f">
              {bucket.label}
            </p>
            <p className="text-[11px] text-white/50 mt-1" data-id="element-2838" data-mp="1hs4ka665yplv">
              {bucket.allowExtras ? <>
                  Incluidas:{' '}
                  <span className="text-white font-semibold" data-id="element-2839" data-mp="1voi61rhqdd65">
                    {bucket.includedCount}
                  </span>
                  {' · '}
                  Extra:{' '}
                  <span className="text-white font-semibold" data-id="element-2840" data-mp="1yqcujyizgm0i">
                    +{formatCurrency(bucket.extraPrice)}
                  </span>{' '}
                  c/u
                </> : <>
                  Selecciona{' '}
                  <span className="text-white font-semibold" data-id="element-2841" data-mp="2di58q771ju0n">
                    {bucket.includedCount}
                  </span>
                </>}
            </p>
          </div>
          {satisfied && total > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-semibold uppercase" data-id="element-2842" data-mp="2mk4pqzi22y3l">
              <CheckIcon className="w-3 h-3" data-id="element-2843" data-mp="2wg7pxztckmnw" />
              Listo
            </span>}
        </div>

        {/* Counter strip */}
        <div className="flex items-center justify-between text-[11px] text-white/60 bg-[#0B0B0B] border border-white/5 rounded-lg px-3 py-2" data-id="element-2844" data-mp="x459kyg0s8eg">
          <span data-id="element-2845" data-mp="124ay45qztesb">
            {total} de {bucket.includedCount}{' '}
            {bucket.allowExtras ? 'incluidas' : 'seleccionadas'}
          </span>
          {extras > 0 ? <span className="text-[#C9A34E] font-semibold" data-id="element-2846" data-mp="3ak6t1vm4eqxp">
              +{extras} extra · +{formatCurrency(extrasTotal)}
            </span> : remaining > 0 ? <span className="text-white/40" data-id="element-2847" data-mp="31m4j2qkesnya">Faltan {remaining}</span> : !bucket.allowExtras && total === bucket.includedCount ? <span className="text-emerald-400 font-semibold" data-id="element-2848" data-mp="23u18iwn9pbl">Máximo</span> : null}
        </div>

        {/* Option list */}
        <div className="space-y-2" data-id="element-2849" data-mp="3pae2jlu5gzm9">
          {bucket.options.map(opt => {
          const qty = (bucketPicks[bucket.id] || {})[opt.id] || 0;
          const canIncrement = bucket.allowExtras || total < bucket.includedCount;
          return <div key={opt.id} className={`flex items-center justify-between gap-3 p-2.5 sm:p-3 rounded-xl border transition-all ${qty > 0 ? 'border-[#C9A34E]/60 bg-[#C9A34E]/5' : 'border-white/10 bg-[#0B0B0B]'}`} data-id="element-2850" data-mp="2dteb0sfmmota">
                <span className="text-sm text-white font-medium truncate min-w-0 flex-1" data-id="element-2851" data-mp="ht40wvg8hd8g">
                  {stripEmbeddedPrice(opt.name)}
                </span>
                <div className="flex items-center gap-2 bg-[#141414] rounded-lg p-1 border border-white/10 shrink-0" data-id="element-2852" data-mp="7twny9devklv">
                  <button type="button" disabled={qty === 0} onClick={() => decrementBucket(bucket, opt)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors" aria-label="Reducir" data-id="element-2853" data-mp="vjhaf28rwzzw">
                    <Minus className="w-4 h-4" data-id="element-2854" data-mp="1ehtmowuzepzp" />
                  </button>
                  <span className="w-5 text-center text-sm font-bold text-white" data-id="element-2855" data-mp="1j9lch56bcros">
                    {qty}
                  </span>
                  <button type="button" disabled={!canIncrement} onClick={() => incrementBucket(bucket, opt)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors" aria-label="Agregar" data-id="element-2856" data-mp="33y5ya0zslrpj">
                    <Plus className="w-4 h-4" data-id="element-2857" data-mp="2l2z3azfz1zaw" />
                  </button>
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-2858" data-mp="2u2vvhb1klgap">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-2859" data-mp="2q224g8stzldl" />
      <div className="relative bg-[#0B0B0B] rounded-3xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-hidden border border-white/10 flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2860" data-mp="99qzlpnmubdy">
        {/* Header */}
        <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-white/10 flex items-start justify-between gap-3 bg-[#141414]" data-id="element-2861" data-mp="3qa2d4a1z596z">
          <div className="min-w-0 flex-1" data-id="element-2862" data-mp="41kdyur8iga4">
            <div className="inline-flex items-center gap-1.5 bg-[#C9A34E] text-[#0B0B0B] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2" data-id="element-2863" data-mp="42c9k5ku0e0e">
              🍣 Combo Kyomu
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white leading-tight" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-2864" data-mp="czmk1optrz49">
              {item.name}
            </h2>
            {item.description && <p className="text-xs text-white/60 mt-1 line-clamp-2" data-id="element-2865" data-mp="ht7jzmazb363">
                {item.description}
              </p>}
            <p className="text-xs text-white/60 mt-1" data-id="element-2866" data-mp="rqvgohs5akqw">
              Armá tu combo por{' '}
              <span className="font-bold text-[#C9A34E]" data-id="element-2867" data-mp="1bcf6e6mp1orf">
                {formatCurrency(Number(item.price) || 0)}
              </span>
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-white/40 hover:text-white p-1 rounded-lg active:scale-95 transition-all bg-white/5 hover:bg-white/10 shrink-0" data-id="element-2868" data-mp="1fd7h74b7s1pt">
            <XIcon className="w-5 h-5" data-id="element-2869" data-mp="ro63b1jm3yd5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-4" data-id="element-2870" data-mp="wfo2pl5zlqw0">
          {loading && otherSlots.length === 0 && mergedBuckets.length === 0 ? <div className="py-10 text-center text-white/40 text-sm" data-id="element-2871" data-mp="ilwv4ibtgh2x">
              Cargando opciones…
            </div> : <>
              {/* Single-pick slots (rolls, onigiri, esfera, mochis…) */}
              {otherSlots.map(slot => {
            const picked = picks[slot.key] || null;
            const isOpenSlot = openSlotKey === slot.key;
            return <div key={slot.key} className="bg-[#141414] border border-white/10 rounded-2xl p-4 space-y-3" data-id="element-2872" data-mp="3lypl481vp44d">
                    <div className="flex items-center justify-between" data-id="element-2873" data-mp="3gzw7mfmldd47">
                      <p className="text-xs font-bold uppercase tracking-wider text-[#C9A34E]" data-id="element-2874" data-mp="3c1uknjm1otng">
                        {slot.label}
                      </p>
                      {picked && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-semibold uppercase" data-id="element-2875" data-mp="8l4bivv620hj">
                          <CheckIcon className="w-3 h-3" data-id="element-2876" data-mp="3ivijziamyff" />
                          Listo
                        </span>}
                    </div>

                    <div data-id="element-2877" data-mp="2obqilgoi91yk">
                      <button type="button" onClick={() => setOpenSlotKey(isOpenSlot ? null : slot.key)} className="w-full bg-[#0B0B0B] border border-white/10 text-sm rounded-xl px-3 py-2.5 text-left flex items-center justify-between gap-2 hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-[#C9A34E]/60 transition-colors" data-id="element-2878" data-mp="2oc8kotxumaee">
                        <span className="min-w-0 flex-1" data-id="element-2879" data-mp="1dkalosg9abib">
                          {picked ? <>
                              <span className="block text-white font-semibold truncate" data-id="element-2880" data-mp="1ik5besoaov1s">
                                {stripEmbeddedPrice(picked.name)}
                              </span>
                              {Number(picked.price_delta) > 0 && <span className="block text-[11px] text-[#C9A34E] mt-0.5" data-id="element-2881" data-mp="1wg91qaw1sj8z">
                                  +{formatCurrency(Number(picked.price_delta))}
                                </span>}
                            </> : <span className="text-white/40" data-id="element-2882" data-mp="1sj3cw0ocghgg">
                              Elegí una opción…
                            </span>}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${isOpenSlot ? 'rotate-180 text-white' : ''}`} data-id="element-2883" data-mp="21kt5vaxvjogc" />
                      </button>

                      {isOpenSlot && <div className="bg-[#0B0B0B] border border-white/10 rounded-xl max-h-72 overflow-y-auto p-1 mt-2" data-id="element-2884" data-mp="14w51hhf2zzpe">
                          {slot.options.map(opt => {
                    const isSelected = picked?.id === opt.id;
                    return <button key={opt.id} type="button" onClick={() => setSlotPick(slot.key, opt)} className="w-full px-3 py-2.5 text-left flex items-start justify-between gap-3 hover:bg-white/5 rounded-lg border-b border-white/5 last:border-0 transition-colors" data-id="element-2885" data-mp="q5e9o9dhp0ma">
                                <span className="min-w-0 flex-1" data-id="element-2886" data-mp="2416hoj3ok6c">
                                  <span className="block text-white text-sm font-semibold" data-id="element-2887" data-mp="70rm6aqztdr8">
                                    {stripEmbeddedPrice(opt.name)}
                                  </span>
                                  {Number(opt.price_delta) > 0 && <span className="block text-[#C9A34E] text-xs mt-0.5" data-id="element-2888" data-mp="kszdhmziccij">
                                      +{formatCurrency(Number(opt.price_delta))}
                                    </span>}
                                </span>
                                {isSelected && <CheckIcon className="w-4 h-4 text-[#C9A34E] shrink-0 mt-0.5" data-id="element-2889" data-mp="3ajcfgalbjv06" />}
                              </button>;
                  })}
                        </div>}
                    </div>
                  </div>;
          })}

              {/* Merged buckets in canonical order: Proteínas → Vegetales → Opciones → Salsas */}
              {mergedBuckets.map(renderBucket)}

              {/* Global notes */}
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-4" data-id="element-2890" data-mp="x9gwgsf3j6x8">
                <p className="text-xs font-bold uppercase tracking-wider text-[#C9A34E] mb-2" data-id="element-2891" data-mp="t8okvsir4pxs">
                  Notas (opcional)
                </p>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Instrucciones especiales para la cocina…" rows={2} className="w-full bg-[#0B0B0B] border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C9A34E]/60 resize-none" data-id="element-2892" data-mp="jcbscxyy0i4m" />
              </div>
            </>}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-white/10 bg-[#141414] space-y-3" data-id="element-2893" data-mp="2v0goekvw8bhb">
          <div className="flex items-center justify-between text-sm" data-id="element-2894" data-mp="2zx74v9bm1939">
            <span className="text-white/60" data-id="element-2895" data-mp="22aq8q7em51j3">Total combo</span>
            <span className="text-2xl font-black text-[#C9A34E]" data-id="element-2896" data-mp="22cb5m3oegwk4">
              {formatCurrency(totalPrice)}
            </span>
          </div>
          <button type="button" disabled={!allPicked} onClick={handleAdd} className={`w-full font-bold text-sm uppercase tracking-wider py-3.5 rounded-2xl transition-all ${allPicked ? 'bg-[#C9A34E] hover:bg-[#B8923D] active:scale-[0.98] text-[#0B0B0B] shadow-lg shadow-[#C9A34E]/20' : 'bg-white/5 text-white/30 cursor-not-allowed'}`} data-id="element-2897" data-mp="2c84pk84g7sbx">
            {allPicked ? 'Agregar combo al pedido' : 'Completá todas las selecciones'}
          </button>
        </div>
      </div>
    </div>;
}